import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import {
  FileText, AlertTriangle, TrendingUp, ArrowRight,
  Clock, ChevronUp, Info, UserPlus,
  Package, Wallet, Activity, ChevronRight, Search, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Format a number as USD currency. Falls back gracefully on undefined. */
const fmt$ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function Dashboard() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  const [insights, setInsights] = useState(null);    // /dashboard/insights
  const [lineItemTypes, setLineItemTypes] = useState([]); // per-business labels
  const [businessInfo, setBusinessInfo] = useState(null); // /info — name + logo + address for the shop status banner
  const [loading, setLoading] = useState(true);
  const [invoiceQuery, setInvoiceQuery] = useState(""); // typeahead filter for Recent activity
  const [searchResults, setSearchResults] = useState([]); // backend-driven typeahead results
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;

  useEffect(() => {
    if (!businessSlug) return;
    let cancelled = false;
    (async () => {
      try {
        // Use allSettled so the dashboard still renders when an aux call
        // (e.g. line-item-types on legacy businesses) returns a transient 404.
        const [insightsRes, typesRes, infoRes] = await Promise.allSettled([
          axios.get(`${API}/dashboard/insights`),
          axios.get(`${API}/line-item-types`),
          axios.get(`${API}/info`),
        ]);
        if (cancelled) return;
        if (insightsRes.status === "fulfilled") setInsights(insightsRes.value.data);
        else console.error("Dashboard insights failed:", insightsRes.reason);
        if (typesRes.status === "fulfilled") {
          setLineItemTypes(typesRes.value.data.line_item_types || []);
        }
        if (infoRes.status === "fulfilled") setBusinessInfo(infoRes.value.data);
      } catch (e) {
        console.error("Dashboard fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backend-driven typeahead — searches ALL invoices (not just the
  // first 6 shown in Recent activity) by invoice number, customer name,
  // or truck number. Debounced 200ms so we don't hammer the API while
  // the user is still typing.
  useEffect(() => {
    if (!businessSlug) return;
    const q = invoiceQuery.trim();
    if (q.length < 1) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const resp = await axios.get(`${API}/invoices/search`, { params: { q, limit: 12 } });
        setSearchResults(resp.data.results || []);
      } catch (e) {
        console.error("Invoice search failed:", e);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [invoiceQuery, businessSlug]); // eslint-disable-line react-hooks/exhaustive-deps


  // BLOCK WHOLESALE/GROCERY — these have their own dashboards.
  // Hooks above stay declared unconditionally so the rules-of-hooks are happy.
  if (user?.business_category === "wholesale") {
    return <Navigate to={`/${user?.business_slug}/wholesale`} replace />;
  }
  if (user?.business_category === "grocery_store") {
    return <Navigate to={`/${user?.business_slug}/grocery`} replace />;
  }

  if (loading || !insights) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const overdue = insights.action_items.overdue_invoices;
  const lowStock = insights.action_items.low_stock;
  const recent = insights.recent_activity;

  const q = invoiceQuery.trim();
  const showingResults = q.length > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? "text-white" : "text-slate-800"}`}>Dashboard</h1>
          <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {insights.period.this_month_label} · last refresh just now
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`${basePath}/financials`}>
            <Button variant="outline" className={isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : ""} data-testid="view-financials-btn">
              <TrendingUp className="w-4 h-4 mr-2" /> View financials
            </Button>
          </Link>
          <Link to={`${basePath}/invoices/new`}>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="create-invoice-btn">
              <FileText className="w-4 h-4 mr-2" /> New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* SHOP STATUS BANNER — front-desk-safe header. Big business logo,
          name, today's day/date, and an "Open" pill. No $ figures, no
          profit numbers — counter staff can stand in front of customers
          without revealing anything sensitive. */}
      <div
        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-slate-700 bg-gradient-to-r from-slate-800 via-slate-800 to-slate-900' : 'border-slate-200 bg-gradient-to-r from-white via-slate-50 to-blue-50'}`}
        data-testid="shop-status-banner"
      >
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          {businessInfo?.logo_url ? (
            <img
              src={businessInfo.logo_url}
              alt={`${businessInfo.name || 'Business'} logo`}
              className="w-16 h-16 rounded-xl object-cover shrink-0 border border-slate-200/40 bg-white"
              data-testid="shop-status-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
              <Activity className="w-8 h-8" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2
                className={`text-2xl sm:text-3xl font-bold truncate ${isDark ? 'text-white' : 'text-slate-800'}`}
                data-testid="shop-status-name"
              >
                {businessInfo?.name || 'Your business'}
              </h2>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200"
                data-testid="shop-status-open"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                Open
              </span>
            </div>
            <div className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {businessInfo?.address ? ` · ${businessInfo.address}` : ''}
            </div>
          </div>
        </div>
      </div>

      {/* MAGAZINE LAYOUT — asymmetric 2/3 + 1/3 split. Cards follow content
          length naturally instead of being forced into equal-height boxes.
          Layout per user request (Feb 2026 swap):
            LEFT (2/3):   Recent Activity (top, primary)
                          Top Categories (below)
            RIGHT (1/3):  Inventory running low (top)
                          Overdue invoices (bottom, where the eye naturally
                          lands after scanning the left column) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-magazine">
        {/* LEFT COLUMN (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Activity — the primary "what's happened today" stream */}
          <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="recent-activity">
            <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3 space-y-3`}>
              <CardTitle className={`text-base flex items-center justify-between ${isDark ? "text-white" : "text-slate-800"}`}>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" /> Recent activity
                </span>
                <Link to={`${basePath}/invoices`} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 font-normal">
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              </CardTitle>
              {/* Typeahead search — queries the WHOLE invoice history
                  on the backend by invoice #, customer, or truck #.
                  Below: when there's a query, we show a suggestions
                  list; otherwise we render the standard Recent feed. */}
              <div className="relative">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
                <Input
                  value={invoiceQuery}
                  onChange={(e) => { setInvoiceQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search ALL invoices by customer, truck #, invoice #…"
                  className={`pl-8 pr-8 h-8 text-sm ${isDark ? "bg-slate-700/50 border-slate-700 placeholder-slate-500 text-white" : "bg-slate-50 border-slate-200 placeholder-slate-400"}`}
                  data-testid="recent-invoices-search"
                />
                {invoiceQuery && (
                  <button
                    type="button"
                    onClick={() => { setInvoiceQuery(""); setSearchResults([]); }}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? "text-slate-400 hover:text-white" : "text-slate-400 hover:text-slate-700"}`}
                    aria-label="Clear search"
                    data-testid="recent-invoices-search-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {showingResults ? (
                /* SEARCH MODE — backend results across the entire history */
                <div data-testid="invoice-search-results">
                  {searching && searchResults.length === 0 ? (
                    <p className={`text-sm text-center py-6 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Searching…</p>
                  ) : searchResults.length === 0 ? (
                    <p className={`text-sm text-center py-6 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      No matches for "{invoiceQuery}".
                    </p>
                  ) : (
                    <div className={`divide-y ${isDark ? "divide-slate-700" : "divide-slate-100"}`}>
                      <div className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-slate-400 bg-slate-700/40" : "text-slate-500 bg-slate-50"}`}>
                        {searchResults.length} match{searchResults.length === 1 ? "" : "es"} across all invoices
                      </div>
                      {searchResults.map((r) => {
                        const isPaid = r.payment_status === "paid";
                        return (
                          <Link
                            key={r.id}
                            to={`${basePath}/invoices/${r.id}`}
                            className={`flex items-center justify-between gap-3 p-3 text-sm transition-colors ${isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50"}`}
                            data-testid={`search-${r.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-blue-600">{r.invoice_number}</span>
                                <Badge className={
                                  isPaid ? "bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0"
                                  : r.payment_status === "partial" ? "bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0"
                                  : "bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0"
                                }>
                                  {(r.payment_status || "pending").toUpperCase()}
                                </Badge>
                                {r.truck_number && (
                                  <span className={`text-[10px] px-1.5 py-0 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                                    Truck {r.truck_number}
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs truncate mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                                {r.customer_name}{r.payment_method ? ` · ${r.payment_method}` : ""}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${isDark ? "text-white" : "text-slate-800"}`}>{fmt$(r.total)}</p>
                              <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                                {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : recent.length === 0 ? (
                <p className={`text-sm text-center py-6 ${isDark ? "text-slate-400" : "text-slate-500"}`}>No invoices yet.</p>
              ) : (
                <div className={`divide-y ${isDark ? "divide-slate-700" : "divide-slate-100"}`}>
                  {recent.slice(0, 6).map((r) => {
                    const isPaid = r.payment_status === "paid";
                    return (
                      <Link
                        key={r.id}
                        to={`${basePath}/invoices/${r.id}`}
                        className={`flex items-center justify-between gap-3 p-3 text-sm transition-colors ${isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50"}`}
                        data-testid={`recent-${r.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-blue-600">{r.invoice_number}</span>
                            <Badge className={
                              isPaid ? "bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0"
                              : r.payment_status === "partial" ? "bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0"
                              : "bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0"
                            }>
                              {(r.payment_status || "pending").toUpperCase()}
                            </Badge>
                          </div>
                          <p className={`text-xs truncate mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                            {r.customer_name}{r.payment_method ? ` · ${r.payment_method}` : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${isDark ? "text-white" : "text-slate-800"}`}>{fmt$(r.total)}</p>
                          <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                            {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* RIGHT COLUMN (1/3) */}
        <div className="lg:col-span-1 space-y-6">
          {/* Inventory running low — sits in the right column above Overdue. */}
          <Card className={`border-l-4 border-l-amber-500 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`} data-testid="low-stock-card">
            <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3`}>
              <CardTitle className={`text-base flex items-center justify-between ${isDark ? "text-white" : "text-slate-800"}`}>
                <span className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-500" />
                  Inventory running low
                  {lowStock.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">
                      {lowStock.length}
                    </Badge>
                  )}
                </span>
                {lowStock.length > 0 && (
                  <Link to={`${basePath}/inventory?stock=low`} className={`text-xs flex items-center gap-1 ${isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-800"}`}>
                    View all <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {lowStock.length === 0 ? (
                <div className={`p-6 text-center text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  All stocked up.
                </div>
              ) : (
                <div className={`divide-y max-h-[280px] overflow-y-auto ${isDark ? "divide-slate-700" : "divide-slate-100"}`}>
                  {lowStock.map((item) => (
                    <Link
                      key={item.id}
                      to={`${basePath}/inventory`}
                      className={`flex items-center justify-between gap-3 p-3 text-sm transition-colors ${isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50"}`}
                      data-testid={`low-stock-${item.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Package className="w-3.5 h-3.5 text-amber-500" />
                          <span className={`font-medium truncate ${isDark ? "text-white" : "text-slate-800"}`}>{item.name}</span>
                        </div>
                        <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>reorder soon</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-600 text-sm">{item.quantity} left</p>
                        <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>min {item.min_stock}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overdue invoices — bottom-right per user request. Rose accent
              border on the left so it visually announces "urgent" without
              shouting. */}
          <Card className={`border-l-4 border-l-rose-500 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`} data-testid="overdue-invoices-card">
            <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3`}>
              <CardTitle className={`text-base flex items-center justify-between gap-2 ${isDark ? "text-white" : "text-slate-800"}`}>
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                  Overdue
                  {overdue.length > 0 && (
                    <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] px-1.5 py-0">
                      {overdue.length}
                    </Badge>
                  )}
                </span>
                {overdue.length > 0 && (
                  <BulkTextButton businessSlug={businessSlug} count={overdue.length} onSent={() => window.location.reload()} />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {overdue.length === 0 ? (
                <div className={`p-6 text-center text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Nothing overdue. Nice.
                </div>
              ) : (
                <div className={`divide-y max-h-[360px] overflow-y-auto ${isDark ? "divide-slate-700" : "divide-slate-100"}`}>
                  {overdue.map((inv) => (
                    <Link
                      key={inv.id}
                      to={`${basePath}/invoices/${inv.id}`}
                      className={`flex items-center justify-between gap-2 p-3 text-sm transition-colors ${isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50"}`}
                      data-testid={`overdue-${inv.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-xs text-blue-600">{inv.invoice_number}</span>
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] px-1.5 py-0">
                            {inv.age_days}d
                          </Badge>
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {inv.customer_name}
                        </p>
                      </div>
                      <p className={`font-bold whitespace-nowrap ${isDark ? "text-white" : "text-slate-800"}`}>{fmt$(inv.total)}</p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}

/** Small "Text all" button shown next to Needs Your Attention when there are overdue invoices. */
function BulkTextButton({ businessSlug, count, onSent }) {
  const [sending, setSending] = useState(false);
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Text payment-link reminders to all ${count} overdue customers?`)) return;
    setSending(true);
    try {
      const resp = await axios.post(`${BASE_API}/b/${businessSlug}/invoices/text-all-overdue`);
      alert(`✓ Sent ${resp.data.sent} reminders (${resp.data.skipped} skipped).`);
      onSent && onSent();
    } catch (err) {
      alert(`Failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSending(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={sending}
      className="text-xs font-normal text-emerald-600 hover:text-emerald-700 underline-offset-2 hover:underline disabled:opacity-50"
      data-testid="bulk-text-overdue-btn"
    >
      {sending ? "Texting…" : `Text all ${count}`}
    </button>
  );
}
